import { useForm, useField } from 'vee-validate';
import { computed } from 'vue';

function createFieldModels(formTemplate, initialsValues = null) {
    const fieldModels = {};

    formTemplate.forEach(({ type, fields }) => {
        if (type !== 'rows') {
            fields.forEach((field) => {
                const { value, errorMessage } = useField(field.model, null, {
                    initialValue: applyInitialValue(
                        field,
                        initialsValues ? initialsValues[field.model] : null
                    )
                });
                fieldModels[field.model] = { value, errorMessage };
            });
        }
    });

    return fieldModels;
}

function applyInitialValue(field, value = null) {
    if (value) {
        return value;
    } else if (field.initialValue || field.initialValue === null) {
        return field.initialValue;
    } else {
        return undefined;
    }
}

function createValidationSchema(formTemplate) {
    const validationSchema = {};
    formTemplate.forEach(({ fields }) => {
        fields.forEach(({ model, rules }) => {
            validationSchema[model] = rules;
        });
    });

    return validationSchema;
}

function countErrors(fieldModels) {
    return computed(() => {
        let errors = 0;

        for (const model in fieldModels) {
            const { errorMessage } = fieldModels[model];
            if (errorMessage.value) {
                errors++;
            }
        }

        return errors;
    });
}

function getMessageErrors(fieldModels) {
    return computed(() => {
        let errors = [];

        for (const model in fieldModels) {
            const { errorMessage } = fieldModels[model];
            if (errorMessage.value) {
                errors.push(errorMessage.value);
            }
        }

        return errors;
    });
}

export default function useCustomForm(template, initialsValues = null) {
    const validationSchema = createValidationSchema(template);
    const { handleSubmit, resetForm, setFieldError } = useForm({
        validationSchema
    });

    const fieldModels = createFieldModels(template, initialsValues);
    const numberErrors = countErrors(fieldModels); // TODO REMOVED
    const messageErrors = getMessageErrors(fieldModels);

    function onValidatedSubmit(callback) {
        handleSubmit((values) => {
            callback(values);
        })();
    }

    function clearForm() {
        resetForm();
    }

    function updatedFromResponse(response) {
        for (const model in fieldModels) {
            let modelKeys = model.split('.');
            let modelValue = response;
            modelKeys.map((key) => {
                modelValue = modelValue[key];
            });
            updateFieldValue(model, modelValue);
        }
    }

    function updateFieldValue(model, value) {
        fieldModels[model]['value']['value'] = value;
    }

    const getModelValue = (model) => {
        return fieldModels[model]?.value?.value;
    };

    function setError(model, errorMessage) {
        setFieldError(model, errorMessage);
    }

    /**
     * Helper function to remove undefined values from object
     * @param {Object} obj - Object to clean
     * @returns {Object} Object without undefined values
     */
    function removeUndefinedValues(obj) {
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
                cleaned[key] = value;
            }
        }
        return cleaned;
    }

    return {
        onValidatedSubmit,
        updateFieldValue,
        updatedFromResponse,
        numberErrors,
        messageErrors,
        fieldModels,
        clearForm,
        getModelValue,
        setError,
        removeUndefinedValues
    };
}
